// Read-only views on the data. Pure functions: easy to test, and the same shape the database
// queries will return later.
import { trendingScore } from '../lib/ranking';
import { normalizeTag } from '../lib/text';
import type { Branch } from '../lib/validation';
import type { Comment, DB, Format, ID, Post, User } from './types';

export type Tab = 'foryou' | 'following' | 'latest';
/** Your own new posts stay on top of For you for an hour, so you see what you just shared. */
export const FRESH_OWN_MS = 3_600_000;
export type FeedFilter = {
  year?: number; tag?: string; branch?: Branch; communityId?: ID; ideaId?: ID; authorId?: ID;
  format?: Format; kindness?: boolean; savedBy?: ID;
};
export type Stats = { reactions: number; comments: number; saves: number };

export const userById = (db: DB, id?: ID): User | undefined => db.users.find(u => u.id === id);
export const userByHandle = (db: DB, h: string): User | undefined => db.users.find(u => u.handle === h.toLowerCase());
export const me = (db: DB): User | undefined => userById(db, db.meId);

// One count per post, built once per version of the data instead of scanning for every post.
const counts = new WeakMap<DB, { r: Map<ID, number>; c: Map<ID, number>; s: Map<ID, number> }>();
function countIndex(db: DB) {
  let idx = counts.get(db);
  if (!idx) {
    const add = (m: Map<ID, number>, k: ID) => m.set(k, (m.get(k) ?? 0) + 1);
    idx = { r: new Map(), c: new Map(), s: new Map() };
    for (const x of db.reactions) add(idx.r, x.postId);
    for (const x of db.comments) if (!x.removed) add(idx.c, x.postId);
    for (const x of db.saves) add(idx.s, x.postId);
    counts.set(db, idx);
  }
  return idx;
}
export function statsFor(db: DB, p: Post): Stats {
  const idx = countIndex(db);
  return {
    reactions: (idx.r.get(p.id) ?? 0) + (p.example ? p.exampleReactions ?? 0 : 0),
    comments: idx.c.get(p.id) ?? 0,
    saves: idx.s.get(p.id) ?? 0,
  };
}

export function visiblePosts(db: DB): Post[] {
  return db.posts.filter(p => !p.removed && !(db.settings.hideExamples && p.example));
}

export function matches(p: Post, f: FeedFilter, db: DB): boolean {
  if (f.year !== undefined && p.year !== f.year) return false;
  if (f.tag && !p.tags.includes(normalizeTag(f.tag))) return false;
  if (f.branch && p.branch !== f.branch) return false;
  if (f.communityId && p.communityId !== f.communityId) return false;
  if (f.ideaId && p.ideaId !== f.ideaId) return false;
  if (f.authorId && p.authorId !== f.authorId) return false;
  if (f.format && p.format !== f.format) return false;
  if (f.kindness && p.branch !== 'Kindness') return false;
  if (f.savedBy && !db.saves.some(s => s.postId === p.id && s.userId === f.savedBy)) return false;
  return true;
}

export function followsPost(db: DB, userId: ID, p: Post): boolean {
  return db.follows.some(f => f.userId === userId && (
    (f.kind === 'user' && f.target === p.authorId) || (f.kind === 'year' && f.target === String(p.year)) ||
    (f.kind === 'tag' && p.tags.includes(f.target)) || (f.kind === 'community' && f.target === p.communityId)));
}

export function feed(db: DB, tab: Tab, f: FeedFilter, now: number): Post[] {
  let list = visiblePosts(db).filter(p => matches(p, f, db));
  const meId = db.meId;
  if (tab === 'following') list = meId ? list.filter(p => p.authorId === meId || followsPost(db, meId, p)) : [];
  if (tab === 'foryou') {
    const score = (p: Post) => {
      if (meId && p.authorId === meId && now - p.createdAt < FRESH_OWN_MS) return Number.MAX_SAFE_INTEGER - (now - p.createdAt);
      const s = statsFor(db, p);
      const boost = meId && followsPost(db, meId, p) ? 1.6 : 1;
      return trendingScore({ createdAt: p.createdAt, ...s }, now) * boost;
    };
    return list.map(p => [score(p), p] as const).sort((a, b) => b[0] - a[0]).map(x => x[1]);
  }
  return list.sort((a, b) => b.createdAt - a.createdAt);
}

/** Hashtags people are using now: count of uses, newer uses weigh more. */
export function trendingTags(db: DB, now: number, limit = 12): { tag: string; uses: number }[] {
  const score = new Map<string, number>(), uses = new Map<string, number>();
  for (const p of visiblePosts(db)) {
    const w = 1 / Math.pow((now - p.createdAt) / 86_400_000 + 1, 0.7);
    for (const t of p.tags) { score.set(t, (score.get(t) ?? 0) + w); uses.set(t, (uses.get(t) ?? 0) + 1); }
  }
  return [...score.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([tag]) => ({ tag, uses: uses.get(tag)! }));
}
export function allTags(db: DB): { tag: string; uses: number }[] {
  const uses = new Map<string, number>();
  for (const p of visiblePosts(db)) for (const t of p.tags) uses.set(t, (uses.get(t) ?? 0) + 1);
  for (const i of db.ideas) for (const t of i.tags) if (!uses.has(t)) uses.set(t, 0);
  return [...uses.entries()].map(([tag, n]) => ({ tag, uses: n })).sort((a, b) => b.uses - a.uses || a.tag.localeCompare(b.tag));
}

/** "I remember this" taps per year, for the bars on the year dial and the leaderboard. */
export function yearCounts(db: DB): Map<number, number> {
  const m = new Map<number, number>();
  for (const p of visiblePosts(db)) m.set(p.year, (m.get(p.year) ?? 0) + statsFor(db, p).reactions + 1);
  return m;
}

export type ThreadNode = { comment: Comment; children: ThreadNode[] };
export function thread(db: DB, postId: ID): ThreadNode[] {
  const list = db.comments.filter(c => c.postId === postId).sort((a, b) => a.createdAt - b.createdAt);
  const byParent = new Map<string, Comment[]>();
  for (const c of list) { const k = c.parentId ?? ''; byParent.set(k, [...(byParent.get(k) ?? []), c]); }
  const build = (pid: string): ThreadNode[] => (byParent.get(pid) ?? []).map(c => ({ comment: c, children: build(c.id) }))
    .filter(n => !n.comment.removed || n.children.length > 0);
  return build('');
}

export function search(db: DB, q: string) {
  const s = q.trim().toLowerCase();
  if (s.length < 2) return { posts: [], users: [], tags: [], communities: [], ideas: [] };
  const tag = normalizeTag(s);
  return {
    posts: visiblePosts(db).filter(p => (p.title + ' ' + p.body).toLowerCase().includes(s) || p.tags.includes(tag) || String(p.year) === s).slice(0, 30),
    users: db.users.filter(u => u.handle.includes(s.replace(/^@/, '')) || u.name.toLowerCase().includes(s)).slice(0, 10),
    tags: allTags(db).filter(t => t.tag.includes(tag)).slice(0, 12),
    communities: db.communities.filter(c => (c.name + ' ' + c.description).toLowerCase().includes(s)).slice(0, 10),
    ideas: db.ideas.filter(i => i.text.toLowerCase().includes(s) || i.tags.includes(tag)).slice(0, 10),
  };
}

export const isFollowing = (db: DB, kind: 'user' | 'tag' | 'year' | 'community', target: string): boolean =>
  !!db.meId && db.follows.some(f => f.userId === db.meId && f.kind === kind && f.target === target);
export const memberRole = (db: DB, communityId: ID) => db.memberships.find(m => m.communityId === communityId && m.userId === db.meId)?.role;
